import React, { useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import useBodyLanguageData from './BodyLanguageData';

const START_TIME_KEYS = [
  'session_start_time',
  'Session_Start_Time',
  'sessionStartTime',
  'start_time',
  'startTime'
];

const BODY_LANGUAGE_KEYS = [
  'body_language',
  'bodyLanguage',
  'BodyLanguage',
  'body_posture',
  'bodyPosture',
  'BodyPosture',
  'posture',
  'Posture',
  'body_language_primary',
  'bodyLanguagePrimary'
];

const getFirstAvailableValue = (obj, keys) => {
  for (const key of keys) {
    if (obj && Object.prototype.hasOwnProperty.call(obj, key) && obj[key] !== undefined && obj[key] !== null) {
      return obj[key];
    }
  }
  return null;
};

const tryParseDate = (value) => {
  if (!value) {
    return null;
  }

  const stringValue = value.toString().trim();
  
  // Handle DD/MM/YYYY HH:mm:ss format (e.g., '17/11/2025 15:34:16')
  const ddmmyyyyPattern = /^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:\s+(\d{1,2}):(\d{1,2}):(\d{1,2}))?$/;
  const match = stringValue.match(ddmmyyyyPattern);
  
  if (match) {
    const day = parseInt(match[1], 10);
    const month = parseInt(match[2], 10) - 1; // JavaScript months are 0-indexed
    const year = parseInt(match[3], 10);
    const hour = match[4] ? parseInt(match[4], 10) : 0;
    const minute = match[5] ? parseInt(match[5], 10) : 0;
    const second = match[6] ? parseInt(match[6], 10) : 0;
    
    const date = new Date(year, month, day, hour, minute, second);
    
    // Validate the date (check if it's a valid date)
    if (date.getFullYear() === year && 
        date.getMonth() === month && 
        date.getDate() === day) {
      return date;
    }
  }
  
  // Fallback to standard Date parsing for other formats
  const date = new Date(stringValue);
  if (!Number.isNaN(date.getTime())) {
    return date;
  }

  return null;
};

const formatDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Bright, shining gradient color for bars
const getShiningGradient = () => {
  return {
    type: 'linear',
    x: 0, y: 1, x2: 0, y2: 0,
    colorStops: [
      { offset: 0, color: '#00ffff' },   // Bright Cyan
      { offset: 0.5, color: '#00ffcc' },  // Vibrant Aqua
      { offset: 1, color: '#00ffaa' }     // Shining Green-Cyan
    ]
  };
};

const getGlowColor = () => {
  return 'rgba(0, 255, 255, 0.8)';   // Bright Cyan glow
};

const CameraDetectionChart = ({ artworkId }) => {
  const { rawData, loading, error } = useBodyLanguageData(artworkId);

  const dateCounts = useMemo(() => {
    if (!rawData || rawData.length === 0) {
      return { dates: [], counts: [] };
    }

    // Filter data where body_language is 'phone'
    const phoneData = rawData.filter((item) => item.body_language === 'phone');

    if (phoneData.length === 0) {
      return { dates: [], counts: [] };
    }

    const dateMap = {};

    phoneData.forEach((item) => {
      const parsedDate = tryParseDate(item.session_start_time);
      if (parsedDate) {
        const dateKey = formatDate(parsedDate);
        dateMap[dateKey] = (dateMap[dateKey] || 0) + 1;
      }
    });
    // Sort dates chronologically
    const sortedEntries = Object.entries(dateMap).sort((a, b) => 
      new Date(a[0]) - new Date(b[0])
    );

    return {
      dates: sortedEntries.map(([date]) => date),
      counts: sortedEntries.map(([, count]) => count)
    };
  }, [rawData]);

  if (loading) {
    return <div className="w-full max-w-3xl bg-gray-950 rounded-xl p-4 shadow-2xl shadow-black/50 backdrop-blur-sm border border-gray-800 flex items-center justify-center h-80 text-center text-white">Loading phone detection data...</div>;
  }

  if (error) {
    return (
      <div className="w-full max-w-3xl bg-gray-950 rounded-xl p-4 shadow-2xl shadow-black/50 backdrop-blur-sm border border-gray-800 flex items-center justify-center h-80 text-center text-red-400">
        {error}
      </div>
    );
  }

  if (!dateCounts.dates.length) {
    return (
      <div className="w-full max-w-3xl bg-gray-950 rounded-xl p-4 shadow-2xl shadow-black/50 backdrop-blur-sm border border-gray-800 flex items-center justify-center h-80 text-center text-gray-300">
        No phone detection data available for this artwork
      </div>
    );
  }

  const option = {
    title: {
      text: 'Carmera Detection',
      left: 'center',
      bottom: '3%',
      textStyle: {
        fontSize: 16,
        fontWeight: 'bold',
        color: '#ffffff'
      }
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'shadow',
        shadowStyle: {
          color: 'rgba(255, 255, 255, 0.1)'
        }
      },
      formatter: (params) => {
        if (!params.length) {
          return '';
        }
        const { name, value } = params[0];
        return `
          <div style="font-weight: 600; margin-bottom: 4px;">${name}</div>
          Records: <span style="color: #00ffff; font-weight: 600;">${value}</span>
        `;
      },
      backgroundColor: 'rgba(0, 0, 0, 0.85)',
      borderColor: 'rgba(255, 255, 255, 0.25)',
      borderWidth: 0,
      textStyle: {
        color: '#fff',
        fontSize: 14
      },
      padding: [10, 15]
    },
    grid: {
      left: '10%',
      right: '10%',
      bottom: '15%',
      top: '10%',
      containLabel: true
    },
    xAxis: {
      type: 'category',
      data: dateCounts.dates,
      axisLabel: {
        fontSize: 12,
        fontWeight: 600,
        color: '#e0e0e0',
        rotate: dateCounts.dates.length > 7 ? 45 : 0
      },
      axisLine: {
        lineStyle: {
          color: '#666666',
          width: 2
        }
      },
      axisTick: {
        show: false
      }
    },
    yAxis: {
      type: 'value',
      // name: 'Number of Records',
      nameTextStyle: {
        fontSize: 12,
        fontWeight: 600,
        color: '#e0e0e0'
      },
      axisLabel: {
        fontSize: 12,
        fontWeight: 600,
        color: '#e0e0e0'
      },
      splitLine: {
        lineStyle: {
          type: 'dashed',
          color: '#444444',
          width: 1
        }
      }
    },
    series: [
      {
        name: 'Records',
        type: 'bar',
        barWidth: '55%',
        data: dateCounts.counts.map((count) => ({
          value: count,
          itemStyle: {
            color: getShiningGradient(),
            borderRadius: [10, 10, 0, 0],
            shadowBlur: 25,
            shadowColor: getGlowColor(),
            shadowOffsetX: 0,
            shadowOffsetY: 8
          },
          emphasis: {
            itemStyle: {
              shadowBlur: 40,
              shadowColor: getGlowColor(),
              shadowOffsetY: 12
            }
          }
        })),
        label: {
          show: true,
          position: 'top',
          formatter: '{c}',
          fontSize: 12,
          fontWeight: 600,
          color: '#ffffff'
        }
      }
    ],
    animationEasing: 'elasticOut',
    animationDelay: (idx) => idx * 120
  };

  return (
    <div className="w-full max-w-3xl bg-gray-950 rounded-xl p-4 shadow-2xl shadow-black/50 backdrop-blur-sm border border-gray-800">
      <div className="h-80">
        <ReactECharts option={option} style={{ height: '100%', width: '100%' }} />
      </div>
    </div>
  );
};

export default CameraDetectionChart;


